import React from 'react';
import { Collapse, Typography } from 'antd';
import { AccordionConfig } from '../../../../types/accordion';
import { ConfigField } from './config-field';
import { ImageGridLayout, RegionGridLayout, isImageGrid } from './grid-layouts';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';

const { Panel } = Collapse;
const { Text } = Typography;

interface ConfigAccordionProps {
  config: AccordionConfig[];
  data: Record<string, any>;
  getFieldLabel: (key: string) => string;
  formatValue: (value: any) => React.ReactNode;
}

export const ConfigAccordion: React.FC<ConfigAccordionProps> = ({ config, data, getFieldLabel, formatValue }) => {
  const { fontSize, spacing } = useResponsiveSizes();

  return (
    <Collapse style={{ backgroundColor: 'transparent' }} ghost>
      {config.map(section => {
        const sectionData: [string, any][] = section.fields
          .map(key => [key, data[key]] as [string, any])
          .filter(([_, value]) => value !== undefined && value !== null && value !== '');

        if (sectionData.length === 0) return null;

        return (
          <Panel
            key={section.key}
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                <div
                  style={{
                    width: 4,
                    height: 20,
                    backgroundColor: section.color,
                    borderRadius: 2
                  }}
                />
                <Text strong style={{ fontSize: fontSize.base }}>
                  {section.title}
                </Text>
              </div>
            }
            style={{
              marginBottom: spacing.sm,
              backgroundColor: '#fafafa',
              borderRadius: 8,
              border: '1px solid #f0f0f0'
            }}
          >
            {section.layout === 'grid' ? (
              <GridDisplay data={sectionData} getFieldLabel={getFieldLabel} formatValue={formatValue} />
            ) : (
              <DefaultDisplay data={sectionData} getFieldLabel={getFieldLabel} formatValue={formatValue} />
            )}
          </Panel>
        );
      })}
    </Collapse>
  );
};

interface DisplayProps {
  data: [string, any][];
  getFieldLabel: (key: string) => string;
  formatValue: (value: any) => React.ReactNode;
}

/**
 * Display por defecto: lista vertical de campos
 */
const DefaultDisplay: React.FC<DisplayProps> = ({ data, getFieldLabel, formatValue }) => {
  const { spacing } = useResponsiveSizes();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
      {data.map(([key, value]) => (
        <ConfigField key={key} keyName={key} value={value} getFieldLabel={getFieldLabel} formatValue={formatValue} />
      ))}
    </div>
  );
};

/**
 * Display con grid: selecciona automáticamente entre ImageGrid y RegionGrid
 */
const GridDisplay: React.FC<DisplayProps> = ({ data, getFieldLabel, formatValue }) => {
  if (isImageGrid(data)) {
    return <ImageGridLayout data={data} getFieldLabel={getFieldLabel} formatValue={formatValue} />;
  }

  return <RegionGridLayout data={data} getFieldLabel={getFieldLabel} formatValue={formatValue} />;
};
